import type { Module, ModuleFolderStructureItem } from "@scayla/api-client-fetch";
import { useQueries } from "@tanstack/vue-query";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { provideRefreshTrigger } from "@/composables/refreshTrigger";
import { localizeBuiltinModule } from "@/i18n/helpers";
import { Routes } from "@/router";
import { useLayoutStore } from "@/store/layoutStore";
import { useWorkspace } from "@/store/workspace";
import { getModuleIcon } from "@/utils/moduleUtils";
import type { NavNode } from "@/utils/navUtils";

import type { FavoriteItem } from "../helpers/moduleNavFavorites";
import { filterModuleForFavorites } from "../helpers/moduleNavFavorites";
import { moduleScopeIds } from "../helpers/moduleScope";
import { isNavigationTemplate } from "../helpers/moduleTemplates";
import { useBinNav } from "./useBinNav";
import { getModuleFoldersStructureQuery } from "./useModuleFoldersStructure";
import { useNavigableModules } from "./useNavigableModules";

export function useModuleNav() {
  const workspace = useWorkspace();
  const layoutStore = useLayoutStore();
  const { t: $t } = useI18n();

  const getModuleKey = (id: string) => `_module_${id}`;

  const modules = useNavigableModules();

  const folderQueries = computed(() =>
    modules.value.map((module) => ({
      ...getModuleFoldersStructureQuery(module.id),
    })),
  );

  function getModulePinnedItems(moduleId: string): FavoriteItem[] {
    const module = workspace.modules?.find((m) => m.id === moduleId);
    const emailId = module && moduleScopeIds(module, workspace.modules ?? []).find((id) => id !== moduleId);
    if (!emailId) return [];

    const emailObj = foldersStructure.value.find((o) => o.moduleId === emailId);
    const emailChildren: NavNode[] = [];

    if (emailObj) {
      let emailTemplates = emailObj.data.templates ?? [];
      emailTemplates = emailTemplates.filter((t) => isNavigationTemplate(t, workspace.getTemplatePermissions(t.id)));
      for (const t of emailTemplates) {
        emailChildren.push({
          label: t.name,
          layoutStoreKey: t.id,
          route: {
            name: Routes.MODULE,
            params: { moduleId: emailId },
            query: { templateId: t.id, tenant: workspace.tenantId },
          },
          indexId: t.indexId,
        });
      }
      for (const f of emailObj.data.folders ?? []) {
        emailChildren.push(mapFolderToNavNode(f, emailId));
      }
      emailChildren.sort((a, b) => (a.indexId ?? Infinity) - (b.indexId ?? Infinity));
    }

    return [
      {
        type: "node",
        data: {
          label: $t("common.label.emails"),
          layoutStoreKey: getModuleKey(emailId),
          route: { name: Routes.MODULE, params: { moduleId: emailId }, query: { tenant: workspace.tenantId } },
          children: emailChildren,
          indexId: -1,
        },
      },
    ];
  }

  const filteredModulesForFavorites = computed(() =>
    modules.value
      .map((module) => filterModuleForFavorites(module, layoutStore, getModuleKey, getModuleTopLevelItems.value))
      .filter((m) => m !== null),
  );

  const folderResults = useQueries({ queries: folderQueries });

  const foldersStructure = computed(() => {
    return folderResults.value
      .map((result, index) => {
        // A failed refetch keeps the last successful data, so `isError` must not hide the module:
        // one transient failure would otherwise empty its whole subtree until the next refetch.
        if (!result.data) {
          return null;
        }

        // `folderQueries` — hence these results — is built from `modules` in order, so the
        // module is known even when the response carries neither a template nor a folder to read it off.
        const moduleId =
          result.data.templates?.[0]?.moduleId ?? result.data.folders?.[0]?.moduleId ?? modules.value[index]?.id;

        if (!moduleId) {
          return null;
        }

        return {
          moduleId,
          data: result.data,
        };
      })
      .filter((item) => item != null);
  });

  const binNav = useBinNav(foldersStructure);

  const refreshFolders = () => {
    folderResults.value.forEach((result) => {
      void result.refetch();
    });
  };

  provideRefreshTrigger(refreshFolders);

  const mapFolderToNavNode = (folder: ModuleFolderStructureItem, moduleId: string): NavNode => {
    const children: NavNode[] = [];

    if (folder.folders?.length) {
      children.push(...folder.folders.map((sub) => mapFolderToNavNode(sub, moduleId)));
    }

    if (folder.templates?.length) {
      for (const template of folder.templates) {
        if (!isNavigationTemplate(template, workspace.getTemplatePermissions(template.id))) continue;

        children.push({
          label: template.name,
          layoutStoreKey: template.id,
          route: {
            name: Routes.MODULE,
            params: { moduleId: moduleId },
            query: { templateId: template.id },
          },
          indexId: template.indexId,
        });
      }
    }

    return {
      label: folder.label,
      layoutStoreKey: folder.id,
      children,
      indexId: folder.indexId,
      route: {
        name: Routes.MODULE,
        params: { moduleId },
        query: { folderId: folder.id },
      },
    };
  };

  const getModuleTopLevelItems = computed<(moduleId: string) => FavoriteItem[]>(() => (moduleId: string) => {
    const pinnedItems: FavoriteItem[] = getModulePinnedItems(moduleId);

    const obj = foldersStructure.value.find((o) => o.moduleId === moduleId);
    if (!obj) return pinnedItems;

    let templates = obj.data.templates ?? [];
    templates = templates.filter((t) => isNavigationTemplate(t, workspace.getTemplatePermissions(t.id)));
    const templateItems: FavoriteItem[] = templates.map((t) => ({ type: "template", data: t }));

    const folderItems: FavoriteItem[] = (obj.data.folders ?? []).map((f) => ({ type: "folder", data: f }));

    const allItems: FavoriteItem[] = [...pinnedItems, ...templateItems, ...folderItems];
    allItems.sort((a, b) => (a.data.indexId ?? Infinity) - (b.data.indexId ?? Infinity));
    return allItems;
  });

  const toModuleNavNode = (module: Module, items: FavoriteItem[]): NavNode => ({
    label: localizeBuiltinModule(module.builtinModule),
    icon: getModuleIcon(module.builtinModule),
    layoutStoreKey: getModuleKey(module.id),
    route: { name: Routes.MODULE, params: { moduleId: module.id }, query: { tenant: workspace.tenantId } },
    children: items
      .map((item) => {
        if (item.type === "template") {
          return {
            label: "name" in item.data ? item.data.name : "",
            layoutStoreKey: item.data.id,
            route: {
              name: Routes.MODULE,
              params: { moduleId: module.id },
              query: { templateId: item.data.id, tenant: workspace.tenantId },
            },
            indexId: item.data.indexId,
          };
        } else if (item.type === "node") {
          return item.data;
        } else {
          return mapFolderToNavNode(item.data, module.id);
        }
      })
      .sort((a, b) => (a.indexId ?? Infinity) - (b.indexId ?? Infinity)),
  });

  const allModuleNodes = computed<NavNode[]>(() =>
    modules.value.map((module) => toModuleNavNode(module, getModuleTopLevelItems.value(module.id))),
  );

  const favoriteModuleNodes = computed<NavNode[]>(() =>
    filteredModulesForFavorites.value.map((module) => toModuleNavNode(module, module.filteredItems ?? [])),
  );

  return { allModuleNodes, favoriteModuleNodes, binNav };
}
